import { useRef, useState, useEffect, useCallback } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import clsx from 'clsx'
import Decimal from 'decimal.js'
import { format } from 'date-fns'
import IconToken from '@/components/icons/IconToken'
import IconNotification from '@/components/icons/IconNotification'
import IconColorNotification from '@/components/icons/IconColorNotification'
import {
  notificationListState,
  unreadNotificationCountValue
} from '@/recoil/Notification'
import { useWalletSelector } from '@/contexts/WalletSelectorContext'
import useOnClickOutside from '@/hooks/useOnClickOutside'
import {
  MIN_NOTIFICATION_SIZE,
  MAX_NOTIFICATION_SIZE,
  NOTIFICATION_STATUS_MAP,
  NOTIFICATION_TEXT_MAP
} from '@/constants/notification'
import {
  ORDER_SIDE,
  ORDER_TYPE_MAP,
  ORDER_TYPE_TEXT_MAP
} from '@/constants/order'
import fetcher from '@/api/fetcher'

import './Notification.css'

export default function Notification() {
  const { accountId } = useWalletSelector()
  const [notificationList, setNotificationList] = useRecoilState(
    notificationListState
  )
  const unreadNotificationCount = useRecoilValue(unreadNotificationCountValue)
  const [isActive, setIsActive] = useState(false)

  const markReadAllNotifications = useCallback(
    () =>
      fetcher
        .markReadAllNotifications()
        .then(() => setNotificationList({ markAllRead: true })),
    [setNotificationList]
  )

  const notificationRef = useRef()
  useOnClickOutside(notificationRef, () => {
    if (!isActive) {
      return
    }
    markReadAllNotifications()
    setIsActive(false)
  })

  useEffect(() => {
    if (!accountId) {
      return
    }
    fetcher.getUnreadNotificationInfo().then(({ count }) => {
      const size = Decimal.max(
        Decimal.min(MAX_NOTIFICATION_SIZE, count),
        MIN_NOTIFICATION_SIZE
      ).toNumber()

      fetcher
        .getNotificationList({
          type: 'TRADE',
          size
        })
        .then(notificationList => {
          const initialList = []
          notificationList.forEach(({ content_raw, mark_read }) => {
            try {
              const data = JSON.parse(content_raw)
              data.initial = true
              data.markRead = mark_read
              data.status = NOTIFICATION_STATUS_MAP.filled
              initialList.push(data)
            } catch (e) {}
          })
          setNotificationList({ initialList })
        })
    })
  }, [accountId, setNotificationList])

  const hasNotification = notificationList.length > 0
  const NotificationList = isActive
    ? notificationList.map(
        (
          {
            orderId,
            tradeId,
            symbol,
            status,
            type,
            side,
            price,
            quantity,
            executedPrice,
            executedQuantity,
            timestamp,
            executedTimestamp,
            reason,
            markRead
          },
          index
        ) => {
          const isNewNotification = markRead === 0
          const [marketType, baseCurrency] = symbol.split('_')
          const marketSymbol =
            marketType === 'PERP' ? `${baseCurrency}-PERP` : baseCurrency
          const isBuyOrder = side === ORDER_SIDE.buy
          const isMarketOrder = ORDER_TYPE_MAP.market === type
          const isFilledOrder = [
            NOTIFICATION_STATUS_MAP.partialFilled,
            NOTIFICATION_STATUS_MAP.filled
          ].includes(status)
          const isRejectedOrder = status === NOTIFICATION_STATUS_MAP.rejected
          const displayPrice = isFilledOrder ? executedPrice : price
          const displaySize = isFilledOrder ? executedQuantity : quantity
          const orderDescription = isMarketOrder
            ? `Size ${displaySize}`
            : `Size ${displaySize} at Price ${displayPrice}`
          const description = isRejectedOrder ? reason : orderDescription
          timestamp = executedTimestamp || timestamp
          return (
            <div
              key={`${orderId}_${status}_${timestamp}_${tradeId}`}
              className="notification"
            >
              <div
                className={clsx('info-row-wrap', {
                  new: isNewNotification
                })}
              >
                <div className="timestamp-label">
                  {isNewNotification && (
                    <div className="new-notification-dot" />
                  )}
                  {format(timestamp, 'yyyy-MM-dd HH:mm:ss')}
                </div>
                <div className="status-label">
                  {NOTIFICATION_TEXT_MAP[status] || ''}
                </div>
              </div>
              <div className="info-row-wrap">
                <div className="market-symbol-label">
                  <IconToken
                    className="market-currency-icon"
                    symbol={baseCurrency}
                  />
                  {marketSymbol}
                </div>
                <div
                  className={clsx('order-type-label', {
                    'color-positive': isBuyOrder,
                    'color-negative': !isBuyOrder
                  })}
                >
                  {ORDER_TYPE_TEXT_MAP[type]} {side.toLowerCase()}
                </div>
              </div>
              <div className="info-row-wrap">{description}</div>
            </div>
          )
        }
      )
    : null

  return (
    <div ref={notificationRef} className="header-icon-button-container">
      <div
        className="header-icon-button noselect"
        onClick={() => {
          isActive && markReadAllNotifications()
          setIsActive(!isActive)
        }}
      >
        {isActive ? (
          <>
            <IconColorNotification />
            <div className="notification-list">
              {hasNotification ? (
                NotificationList
              ) : (
                <div className="empty-list-view">
                  <IconNotification className="notification-icon" />
                  You have no notifications
                </div>
              )}
            </div>
          </>
        ) : (
          <div
            className={clsx('icon-wrap', {
              'has-unread': unreadNotificationCount > 0
            })}
          >
            <IconNotification />
          </div>
        )}
      </div>
    </div>
  )
}
